/**
 * Cross-System Signals — calm awareness band on the Executive Dashboard.
 *
 * Purpose: distilled cross-domain situational awareness for the
 * "Hub" layer. NOT analytics, NOT operational details. Just three
 * small calm cards routing into the deeper domain dashboards:
 *
 *   1. Territory Movement       → Terra Growth OS™
 *   2. Stewardship Sustainability → Sustainability
 *   3. Intelligence Drift        → Intelligence
 *
 * Single backend reuse: GET /api/terra-growth/war-room?days=7
 * Zero new endpoints, zero new logic.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ChevronRight } from 'lucide-react';

const Card = ({ testid, eyebrow, title, lines, link, linkLabel }) => (
  <Link
    to={link}
    data-testid={testid}
    className="group block rounded-2xl bg-white/80 hover:bg-white border border-[#E2DCCB] hover:border-[#C8A368] transition-all p-5 sm:p-6"
  >
    <div className="flex items-baseline gap-2 mb-3">
      <span className="w-1 h-1 rounded-full bg-[#8C6239]" />
      <p className="text-[9.5px] uppercase tracking-[0.22em] text-[#8C6239] font-['Work_Sans']">
        {eyebrow}
      </p>
    </div>
    <h3 className="font-['Outfit'] font-light text-[#1A1816] text-lg sm:text-xl leading-snug mb-3">
      {title}
    </h3>
    <ul className="space-y-1.5 mb-5">
      {lines.length === 0 ? (
        <li className="text-[12px] text-[#8A8681] italic font-['Work_Sans']">
          Calm rhythm — nothing asking for attention this week.
        </li>
      ) : (
        lines.map((l, i) => (
          <li
            key={i}
            className="text-[12.5px] text-[#3C3A37] font-['Work_Sans'] leading-relaxed flex gap-2"
          >
            <span className="text-[#7E8E72] mt-1.5 w-1 h-1 rounded-full bg-[#7E8E72] flex-shrink-0" />
            <span>{l}</span>
          </li>
        ))
      )}
    </ul>
    <span className="text-[10.5px] text-[#5C3D1F] font-['Work_Sans'] tracking-wide inline-flex items-center gap-1 opacity-70 group-hover:opacity-100">
      {linkLabel}
      <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
    </span>
  </Link>
);

export default function CrossSystemSignals() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/terra-growth/war-room?days=7`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  const signals = useMemo(() => deriveSignals(data), [data]);

  // Quietly hide the band if war-room access fails (e.g. role lacks access).
  // The rest of the dashboard continues to work.
  if (error) return null;

  return (
    <section
      data-testid="cross-system-signals"
      className="terra-band rounded-3xl px-6 py-7 sm:px-8 sm:py-9 mb-2 border border-[#E2DCCB]"
      style={{
        background:
          "radial-gradient(900px 400px at 8% -10%, rgba(232,220,196,0.55) 0%, rgba(232,220,196,0) 60%)," +
          "radial-gradient(700px 360px at 100% 110%, rgba(181,194,168,0.30) 0%, rgba(181,194,168,0) 55%)," +
          "#FBF9F4",
        boxShadow: "0 1px 0 rgba(140, 98, 57, 0.04), 0 1px 2px rgba(31, 29, 27, 0.03)",
      }}
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#8C6239] font-['Work_Sans'] mb-1.5">
            Cross-System Signals
          </p>
          <h2 className="font-['Outfit'] font-light text-[#1A1816] text-xl sm:text-2xl leading-tight">
            What deserves awareness today.
          </h2>
        </div>
        <p className="text-[10.5px] italic text-[#8A8681] font-['Work_Sans'] max-w-md">
          A calm orchestration layer — never duplication of the deeper modules,
          only what the platform is quietly asking you to look toward.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
        <Card
          testid="signal-territory-movement"
          eyebrow="Territory Movement"
          title={signals.territory.title}
          lines={signals.territory.lines}
          link="/admin/growth-command-center"
          linkLabel="Open Terra Growth OS™"
        />
        <Card
          testid="signal-stewardship-sustainability"
          eyebrow="Stewardship Sustainability"
          title={signals.stewardship.title}
          lines={signals.stewardship.lines}
          link="/sustainability/team"
          linkLabel="Open Sustainability"
        />
        <Card
          testid="signal-intelligence-drift"
          eyebrow="Intelligence Drift"
          title={signals.intelligence.title}
          lines={signals.intelligence.lines}
          link="/intelligence/movement-digest"
          linkLabel="Open Intelligence"
        />
      </div>

      {!data && (
        <p className="text-[10.5px] text-[#8A8681] italic font-['Work_Sans'] mt-4">
          listening to the territory…
        </p>
      )}
    </section>
  );
}

// ── Signal derivation (deterministic; pure read of war-room snapshot) ──
function deriveSignals(d) {
  const empty = {
    territory:    { title: '—', lines: [] },
    stewardship:  { title: '—', lines: [] },
    intelligence: { title: '—', lines: [] },
  };
  if (!d) return empty;

  const ep = d.ecosystem_pulse || {};
  const tc = d.territory_calibration || {};
  const hs = d.human_stewardship || {};
  const mi = d.movement_integrity || {};

  // ── Territory Movement ──
  const fwd  = ep.movements?.forwards ?? 0;
  const regr = ep.movements?.regressions ?? 0;
  const stuck = ep.stuck_total ?? 0;
  const slowing = (ep.movement_slowing_zones || []).length;
  const tCalibration = (tc.centres || []).filter(c => c.confidence === 'LOW').length;
  let tTitle;
  const tLines = [];
  if (fwd === 0 && regr === 0 && stuck === 0) {
    tTitle = 'Quiet rhythm holding.';
  } else if (fwd > regr && stuck === 0) {
    tTitle = 'Quiet growth emerging.';
  } else if (slowing > 0) {
    tTitle = 'Movement rhythm softening.';
  } else if (tCalibration > 0) {
    tTitle = 'Calibration may help in some territories.';
  } else {
    tTitle = 'Stabilization rhythm holding.';
  }
  if (fwd > 0) tLines.push(`${fwd} forward movement${fwd === 1 ? '' : 's'} this week.`);
  if (regr > 0) tLines.push(`${regr} quiet regression${regr === 1 ? '' : 's'} — softening, not failure.`);
  if (stuck > 0) tLines.push(`${stuck} lead${stuck === 1 ? '' : 's'} resting in place.`);
  if (slowing > 0) tLines.push(`${slowing} slowing zone${slowing === 1 ? '' : 's'} observed.`);
  if (tCalibration > 0)
    tLines.push(`${tCalibration} centre${tCalibration === 1 ? '' : 's'} reading at low confidence.`);

  // ── Stewardship Sustainability ──
  const restore = (hs.needing_restoration || []).length;
  const refSoft = (hs.reflective_space_softening || []).length;
  const recovSoft = (hs.recovery_rhythm_softening || []).length;
  const ment = hs.mentorship?.pairing_count ?? 0;
  let sTitle;
  const sLines = [];
  if (restore === 0 && refSoft === 0 && recovSoft === 0) {
    sTitle = 'Recovery rhythm stable.';
  } else if (restore > 0) {
    sTitle = 'Restoration support suggested.';
  } else if (refSoft > 0) {
    sTitle = 'Reflective space compression observed.';
  } else {
    sTitle = 'Recovery rhythm softening.';
  }
  if (restore > 0)
    sLines.push(`Restoration support suggested for ${restore} counselor${restore === 1 ? '' : 's'}.`);
  if (refSoft > 0)
    sLines.push(`${refSoft} counselor${refSoft === 1 ? '' : 's'} with reflective space softening.`);
  if (recovSoft > 0)
    sLines.push(`${recovSoft} with recovery rhythm softening.`);
  if (ment > 0)
    sLines.push(`${ment} mentorship pairing${ment === 1 ? '' : 's'} active — quietly held.`);

  // ── Intelligence Drift ──
  const depthDelta = mi.depth_quality_shift?.delta ?? 0;
  const fp  = mi.false_positive_movement_risk ?? 0;
  const lkD = mi.leakage_stabilization?.delta ?? 0;
  const fam = mi.family_alignment_softening ?? 0;
  let iTitle;
  const iLines = [];
  if (depthDelta === 0 && fp === 0 && fam === 0 && lkD === 0) {
    iTitle = 'Stabilization momentum holding.';
  } else if (depthDelta < 0 || fp > 0) {
    iTitle = 'Ecosystem softening detected.';
  } else if (fam > 0) {
    iTitle = 'Family alignment narrowing.';
  } else {
    iTitle = 'Evaluation band widening.';
  }
  if (depthDelta !== 0) {
    const sign = depthDelta > 0 ? '+' : '';
    iLines.push(`Depth quality shift ${sign}${depthDelta.toFixed(1)}%.`);
  }
  if (fp > 0)
    iLines.push(`${fp} movement${fp === 1 ? '' : 's'} flagged for false-positive review.`);
  if (lkD !== 0) {
    const sign = lkD > 0 ? '+' : '';
    iLines.push(`Leakage stabilization ${sign}${lkD}.`);
  }
  if (fam > 0)
    iLines.push(`${fam} family alignment softening signal${fam === 1 ? '' : 's'}.`);

  return {
    territory:    { title: tTitle, lines: tLines.slice(0, 3) },
    stewardship:  { title: sTitle, lines: sLines.slice(0, 3) },
    intelligence: { title: iTitle, lines: iLines.slice(0, 3) },
  };
}
